import React, { useState, useCallback, useMemo } from "react";
import { useDropzone, Accept, FileRejection } from "react-dropzone";
import styled, { css } from "styled-components";
import toast from "react-hot-toast";

import Button from "../button/Button";
import ImageEditorModal from "../image-editor/ImageEditorModal";
import AiImageGeneratorModal from "../ai-image-generator/AiImageGeneratorModal";
import type { AiImageGeneratorModalProps } from "../ai-image-generator/AiImageGeneratorModal";
import type { GeneratedImageCandidate } from "../../app/services/aiImage";
import type { CropData, CropResult } from "../image-editor/cropUtils";
import { blobToDataUrl, fetchImageBlob } from "../image-editor/cropUtils";
import {
  getInitialAspectSelection,
  imageShapeForAspect,
  type AspectRule,
} from "../image-editor/aspectRule";

/** 表示中の画像 */
export interface ImagePickerValue {
  /** 表示用の URL（切り抜き後） */
  url: string;
  /** 切り抜き前の元画像 URL（再クロップ用。ない場合は url で代用する） */
  sourceUrl?: string | null;
  /** 元画像に対する前回の切り抜き座標 */
  cropData?: CropData | null;
}

/** 確定した画像の元画像がどこから来たか */
export type ImagePickerSource =
  /** 新しく選んだファイル */
  | { type: "file"; file: File }
  /** AI 生成（S3 アップロード済み） */
  | { type: "key"; fileKey: string; url: string }
  /** 既存の元画像を切り直した */
  | null;

export interface ImagePickerChange {
  /** 保存する画像（切り抜き後。crop=false のときは選んだファイルそのもの） */
  file: File;
  /** file の dataURL */
  base64: string;
  /** 元画像に対する切り抜き座標（aspect を含む）。crop=false のときは null */
  cropData: CropData | null;
  source: ImagePickerSource;
}

export interface ImagePickerProps {
  /** 表示中の画像。省略すると内部で保持する（保存しないモック用途） */
  value?: ImagePickerValue | null;
  /** 比率の制約（例: ASPECT_RULES.projectBanner） */
  aspectRule: AspectRule;
  /** クロップ形状 */
  cropShape?: "rect" | "round";
  /** false ならクロップせず、選んだファイルをそのまま onChange に渡す */
  crop?: boolean;
  /** 出力の長辺の上限 px（比率を保って縮小する） */
  maxOutputEdge?: number;
  /** 出力幅（aspectRule が fixed のときのみ） */
  outputWidth?: number;
  /** 出力高さ（aspectRule が fixed のときのみ） */
  outputHeight?: number;
  /** 出力フォーマット */
  outputFormat?: "image/jpeg" | "image/png" | "image/webp";
  /** 出力ファイル名 */
  outputFileName?: string;
  /** 受け入れる MIME タイプ。default: image のみ */
  accept?: Accept;
  /** 選べるファイルサイズの上限（byte） */
  maxBytes?: number;
  /** プレビューの高さの上限。縦長でも比率を保ったまま幅が縮む */
  size?: "small" | "medium" | "large";
  /**
   * 指定すると「AIで生成」を表示する。imageShape を省略すると、
   * 比率の制約の初期値から square / portrait / landscape を決める。
   */
  aiGenerator?: Omit<AiImageGeneratorModalProps, "onSelect" | "onClose">;
  /** 画像の確定時（Promise を返すと完了まで操作を無効化する） */
  // eslint-disable-next-line no-unused-vars
  onChange?: (result: ImagePickerChange) => void | Promise<void>;
  /** 指定すると「削除」を表示する（Promise を返すと完了まで操作を無効化する） */
  onRemove?: () => void | Promise<void>;
  disabled?: boolean;
}

interface EditingState {
  src: string;
  source: ImagePickerSource;
  cropData: CropData | null;
}

const PREVIEW_MAX_HEIGHT: Record<NonNullable<ImagePickerProps["size"]>, number> = {
  small: 96,
  medium: 160,
  large: 256,
};

const FILE_EXTENSIONS: Record<NonNullable<ImagePickerProps["outputFormat"]>, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

/** 空のときの比率（「そのまま」が初期値なら 16:9 の枠で出す） */
const EMPTY_FALLBACK_ASPECT = 16 / 9;

// 幅は「100%」と「高さの上限 × 比率」の小さい方。細いバナーは幅に、縦長は高さに合わせて縮む
const Dropzone = styled.button.attrs({ type: "button", className: "bodytext-13" })<{
  $aspect: number;
  $maxHeight: number;
  $active: boolean;
}>`
  width: min(100%, ${({ $maxHeight, $aspect }) => $maxHeight * $aspect}px);
  aspect-ratio: ${({ $aspect }) => $aspect};
  min-height: 56px;
  border: 1px dashed var(--soremo-border);
  border-radius: 6px;
  padding: 8px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  background-color: var(--soremo-background);
  transition: background-color 0.3s ease;
  color: var(--soremo-light-gray);

  .material-symbols-rounded {
    color: var(--soremo-border);
  }

  &:hover:not(:disabled),
  &:focus-visible {
    background-color: var(--soremo-blue);
    border: 1px solid var(--soremo-border);
    color: #fff;

    .material-symbols-rounded {
      color: #fff;
    }
  }

  &:disabled {
    cursor: default;
  }

  ${({ $active }) =>
    $active &&
    css`
      border: 1px solid var(--soremo-blue);
    `}
`;

const PreviewButton = styled.button.attrs({ type: "button" })<{
  $maxHeight: number;
  $active: boolean;
  $busy: boolean;
}>`
  display: inline-flex;
  max-width: 100%;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  opacity: ${({ $busy }) => ($busy ? 0.6 : 1)};
  transition: opacity 0.2s ease;

  img {
    display: block;
    max-width: 100%;
    max-height: ${({ $maxHeight }) => $maxHeight}px;
    border-radius: 6px;
  }

  &:disabled {
    cursor: default;
  }

  ${({ $active }) =>
    $active &&
    css`
      border-color: var(--soremo-blue);
    `}
`;

const formatMegabytes = (bytes: number) => `${Math.floor(bytes / (1024 * 1024))}MB`;

/**
 * 画像 1 枚を設定する統一コンポーネント（選択 / D&D / AI 生成 → クロップ → 確定、
 * 既存画像の切り抜き編集・変更・削除）。
 *
 * アップロードや保存はしない。確定した画像を `onChange` で返すので、即時保存と
 * フォーム送信時の保存のどちらにも使える。比率の制約は `aspectRule` で渡す。
 */
const ImagePicker: React.FC<ImagePickerProps> = ({
  value,
  aspectRule,
  cropShape = "rect",
  crop = true,
  maxOutputEdge,
  outputWidth,
  outputHeight,
  outputFormat = "image/jpeg",
  outputFileName,
  accept = { "image/*": [] },
  maxBytes,
  size = "medium",
  aiGenerator,
  onChange,
  onRemove,
  disabled = false,
}) => {
  const [internalValue, setInternalValue] = useState<ImagePickerValue | null>(null);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);

  const isControlled = value !== undefined;
  const current = isControlled ? value : internalValue;
  const displayUrl = pendingUrl ?? current?.url ?? null;
  const isDisabled = disabled || busy;
  const maxHeight = PREVIEW_MAX_HEIGHT[size];

  const preferredAspect = useMemo(() => {
    const selection = getInitialAspectSelection(aspectRule, current?.cropData);
    return selection.mode === "original" ? null : selection.aspect;
  }, [aspectRule, current?.cropData]);

  const fileName = outputFileName ?? `image.${FILE_EXTENSIONS[outputFormat]}`;

  /**
   * 確定した画像を親へ渡す（Promise の間は操作を止め、プレビューは先に差し替える）。
   * `nextSourceUrl` は value を渡されていない（内部で保持する）ときの再クロップ用。
   */
  const commit = useCallback(
    async (result: ImagePickerChange, nextSourceUrl: string | null) => {
      setPendingUrl(result.base64);
      setBusy(true);
      try {
        await onChange?.(result);
        if (!isControlled) {
          setInternalValue({
            url: result.base64,
            sourceUrl: nextSourceUrl,
            cropData: result.cropData,
          });
        }
      } catch (error) {
        console.error("Image change failed:", error);
      } finally {
        setPendingUrl(null);
        setBusy(false);
      }
    },
    [onChange, isControlled],
  );

  const handleFile = useCallback(
    async (file: File) => {
      const dataUrl = await blobToDataUrl(file);
      const source: ImagePickerSource = { type: "file", file };
      if (crop) {
        setEditing({ src: dataUrl, source, cropData: null });
      } else {
        await commit({ file, base64: dataUrl, cropData: null, source }, dataUrl);
      }
    },
    [crop, commit],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[], rejections: FileRejection[]) => {
      if (rejections.length > 0 && acceptedFiles.length === 0) {
        toast.error("画像ファイルを指定してください");
        return;
      }
      const file = acceptedFiles[0];
      if (!file) return;
      if (maxBytes && file.size > maxBytes) {
        toast.error(`画像は ${formatMegabytes(maxBytes)} 未満にしてください`);
        return;
      }
      handleFile(file).catch((error) => {
        console.error("Failed to read image:", error);
        toast.error("画像を読み込めませんでした");
      });
    },
    [maxBytes, handleFile],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    disabled: isDisabled,
  });

  const handleEditExisting = useCallback(() => {
    if (!current) return;
    if (!crop) {
      open();
      return;
    }
    setEditing({
      src: current.sourceUrl || current.url,
      source: null,
      // 元画像がない（表示中の画像で代用する）ときは前回の座標が合わないので使わない
      cropData: current.sourceUrl ? (current.cropData ?? null) : null,
    });
  }, [current, crop, open]);

  const handleEditorConfirm = useCallback(
    async (result: CropResult) => {
      if (!editing) return;
      const { source } = editing;
      const nextSourceUrl =
        source === null
          ? (current?.sourceUrl ?? current?.url ?? null)
          : source.type === "key"
            ? source.url
            : editing.src;
      setEditing(null);
      await commit(
        { file: result.file, base64: result.base64, cropData: result.cropData, source },
        nextSourceUrl,
      );
    },
    [editing, current, commit],
  );

  const handleAiSelect = useCallback(
    async (candidate: GeneratedImageCandidate) => {
      const source: ImagePickerSource = {
        type: "key",
        fileKey: candidate.file_key,
        url: candidate.url,
      };
      // 取得に失敗したら throw してモーダル側でエラートーストを出す
      const blob = await fetchImageBlob(candidate.url);
      const dataUrl = await blobToDataUrl(blob);
      setShowAiModal(false);
      if (crop) {
        setEditing({ src: dataUrl, source, cropData: null });
        return;
      }
      const file = new File([blob], fileName, { type: blob.type || outputFormat });
      await commit({ file, base64: dataUrl, cropData: null, source }, candidate.url);
    },
    [crop, fileName, outputFormat, commit],
  );

  const handleRemove = useCallback(async () => {
    setBusy(true);
    try {
      await onRemove?.();
      if (!isControlled) setInternalValue(null);
    } catch (error) {
      console.error("Image remove failed:", error);
    } finally {
      setBusy(false);
    }
  }, [onRemove, isControlled]);

  // value を渡されていない（内部で保持する）ときは、onRemove がなくても外せるようにする
  const showRemove = Boolean(onRemove) || !isControlled;

  return (
    <div className="u-col u-gap8 u-w100" {...getRootProps()}>
      <input {...getInputProps()} />

      <div className="u-row-center u-w100">
        {displayUrl ? (
          <PreviewButton
            $maxHeight={maxHeight}
            $active={isDragActive}
            $busy={busy}
            disabled={isDisabled}
            onClick={handleEditExisting}
            aria-label={crop ? "切り抜きを編集" : "画像を変更"}
          >
            <img src={displayUrl} alt="" />
          </PreviewButton>
        ) : (
          <Dropzone
            $aspect={preferredAspect ?? EMPTY_FALLBACK_ASPECT}
            $maxHeight={maxHeight}
            $active={isDragActive}
            disabled={isDisabled}
            onClick={open}
            aria-label="画像を選択"
          >
            <span className="material-symbols-rounded u-fontsize-48">add_a_photo</span>
            {isDragActive && <span>ここにファイルをドロップしてください</span>}
          </Dropzone>
        )}
      </div>

      {(displayUrl || aiGenerator) && (
        <div className="u-row-between u-w100 u-gap8 u-wrap">
          <div className="u-row u-gap8 u-wrap">
            {displayUrl && crop && (
              <Button
                size="small"
                theme="secondary"
                width="fit"
                leftIcon="crop"
                disabled={isDisabled}
                onClick={handleEditExisting}
              >
                切り抜きを編集
              </Button>
            )}
            {displayUrl && (
              <Button
                size="small"
                theme="secondary"
                width="fit"
                leftIcon="upload"
                disabled={isDisabled}
                onClick={open}
              >
                画像を変更
              </Button>
            )}
            {displayUrl && showRemove && (
              <Button
                size="small"
                theme="tertiary"
                width="fit"
                leftIcon="delete"
                disabled={isDisabled}
                onClick={handleRemove}
              >
                削除
              </Button>
            )}
          </div>
          {aiGenerator && (
            <Button
              size="small"
              theme="secondary"
              width="fit"
              leftIcon="auto_awesome"
              disabled={isDisabled}
              onClick={() => setShowAiModal(true)}
            >
              AIで生成
            </Button>
          )}
        </div>
      )}

      {editing && (
        <ImageEditorModal
          imageSrc={editing.src}
          aspectRule={aspectRule}
          cropShape={cropShape}
          maxOutputEdge={maxOutputEdge}
          outputWidth={outputWidth}
          outputHeight={outputHeight}
          outputFormat={outputFormat}
          outputFileName={fileName}
          initialCropData={editing.cropData}
          onConfirm={handleEditorConfirm}
          onCancel={() => setEditing(null)}
          onReselect={open}
        />
      )}

      {showAiModal && aiGenerator && (
        <AiImageGeneratorModal
          {...aiGenerator}
          imageShape={aiGenerator.imageShape ?? imageShapeForAspect(preferredAspect)}
          onSelect={handleAiSelect}
          onClose={() => setShowAiModal(false)}
        />
      )}
    </div>
  );
};

export default ImagePicker;
